import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelected } from "../contexts/SelectedContext";
import { useTheme } from "../contexts/ThemeContext";
import { MdLogout, MdSettings, MdAdminPanelSettings } from "react-icons/md";

// Import des constantes de thème et de la fonction utilitaire
import { THEMES, THEME_COLORS, getLogoPath } from "./Constants"; 

// Import de toutes les images de logo nécessaires
import logo_row_blue from '../images/logo_row_blue.png';
import logo_row_green from '../images/logo_row_green.png';
import logo_row_red from '../images/logo_row_red.png'
import logo_row_orange from '../images/logo_row_orange.png'
import logo_row_yellow from '../images/logo_row_yellow.png'


// Mapping pour récupérer l'image d'importation réelle
const LOGO_IMAGES = {
    'logo_row_blue.png': logo_row_blue,
    'logo_row_green.png': logo_row_green,
    'logo_row_red.png': logo_row_red,
    'logo_row_orange.png': logo_row_orange,
    'logo_row_yellow.png': logo_row_yellow,
};

// Suppression des constantes de couleur locales (elles viennent de Constants.jsx)

export default function Topbar({ 
    currentUser, 
    persons, 
    accounts, 
    selectedYear, 
    setSelectedYear,
    onLogout,
    availableYears = []
}) {
    const { selectedPerson, setSelectedPerson, selectedAccount, setSelectedAccount } = useSelected();
    const { currentTheme } = useTheme(); // <-- Récupération du thème et du toggle
    const location = useLocation(); 

    // Récupération dynamique des couleurs du thème actuel
    const themeColors = THEME_COLORS[currentTheme] || THEME_COLORS[THEMES.BLUE];

    // Calcul de l'image de logo
    const logoFileName = getLogoPath(currentTheme, 'row');
    const LogoImage = LOGO_IMAGES[logoFileName];

    // Test de sécurité explicite
    const safePersons = Array.isArray(persons) ? persons : [];
    const isAdmin = currentUser?.role === 'Admin' || currentUser?.originalRole === 'Admin';
    const isAssistanceMode = !!currentUser?.adminMode;
    const adminPath = `/admin-${currentUser?.adminPath || "protected-default-path"}`;

    // La ligne de calcul
    const managedPersons = safePersons.filter(p => p.is_managed === true || p.is_managed === 1);

    // Déterminer les années disponibles
    const currentYear = new Date().getFullYear();
    let yearsSet = new Set([currentYear, currentYear + 1]);
    availableYears.forEach(year => yearsSet.add(year));

    const maxFutureYear = currentYear + 1;
    
    const years = Array.from(yearsSet)
        .filter(year => year <= maxFutureYear)
        .sort((a, b) => b - a);

    const handlePersonChange = (e) => {
        const personId = parseInt(e.target.value);
        const person = persons.find(p => p.id === personId);
        setSelectedPerson(person);
        // Réinitialise le compte si la personne change
        const defaultAccount = accounts.find(acc => acc.personId === personId);
        setSelectedAccount(defaultAccount || accounts[0]);
    };

    const handleAccountChange = (e) => {
        const accountId = parseInt(e.target.value);
        const account = accounts.find(acc => acc.id === accountId);
        setSelectedAccount(account);
    };
    
    // 💡 Fonction utilitaire pour le style actif du lien (utilise themeColors)
    const getLinkStyle = (path) => {
        const isActive = location.pathname === path;
        return {
            ...styles.link,
            color: isActive ? themeColors.secondary : themeColors.text, // Couleur secondaire si actif
            borderBottom: isActive ? `3px solid ${themeColors.primary}` : '3px solid transparent', // Ligne de couleur en dessous
            fontWeight: isActive ? "700" : "600",
        };
    };

    return (
        <header style={{ 
            ...styles.topbar, 
            backgroundColor: isAssistanceMode ? '#2c3e50' : themeColors.background, // 💡 Couleur sombre en mode assistance
            borderBottom: isAssistanceMode ? '3px solid #e74c3c' : 'none' // 💡 Bordure rouge pour alerter
        }}>
            <div style={styles.logoContainer}>
                <img 
                    src={LogoImage} 
                    alt="Mon Budget App Logo" 
                    style={styles.headerLogoImage} 
                />
                {/* 💡 Indicateur visuel du mode assistance */}
                {isAssistanceMode && (
                    <div style={styles.assistanceBadge}>
                        MODE ASSISTANCE : {currentUser.name}
                    </div>
                )}
            </div>

            {/* Liens de navigation au centre */}
            <nav style={styles.links}>
                {isAdmin && (
                    <Link to={adminPath} style={getLinkStyle(adminPath)}>
                        <MdAdminPanelSettings size={18} style={{ marginRight: '5px' }} />
                        ADMIN
                    </Link>
                )}
                <Link to="/comptes" style={getLinkStyle("/comptes")}>
                    Comptes
                </Link>
                <Link to="/dashboard" style={getLinkStyle("/dashboard")}>
                    Accueil
                </Link>
                <Link to="/dettes" style={getLinkStyle("/dettes")}>
                    Dettes
                </Link>
                <Link to="/recurrence" style={getLinkStyle("/recurrence")}>
                    Récurrences
                </Link>
                <Link to="/situation" style={getLinkStyle("/situation")}>
                    Situation
                </Link>
            </nav>

            {/* Listes déroulantes et sélecteur de thème à droite */}
            <div style={styles.dropdowns}>
                {/* SÉLECTEUR DE PERSONNE */}
                <select 
                    value={selectedPerson ? selectedPerson.id : ""} 
                    onChange={handlePersonChange} 
                    style={{ 
                        ...styles.select, 
                        border: `1px solid ${themeColors.secondary}`, // Couleur dynamique
                        background: themeColors.inputBackground, // Couleur dynamique
                        color: themeColors.text, // Couleur dynamique
                    }}
                >
                    <option value="" disabled>Sélectionner une personne</option>
                    {managedPersons.map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.name}
                        </option>
                    ))}
                </select>

                {/* SÉLECTEUR DE COMPTE */}
                <select 
                    value={selectedAccount ? selectedAccount.id : ""} 
                    onChange={handleAccountChange} 
                    disabled={!selectedPerson}
                    style={{ 
                        ...styles.select,
                        border: `1px solid ${themeColors.secondary}`, // Couleur dynamique
                        background: themeColors.inputBackground, // Couleur dynamique
                        color: themeColors.text, // Couleur dynamique
                    }}
                >
                    <option value="" disabled>{selectedPerson ? "Sélectionner un compte" : "Choisir d'abord une personne"}</option>
                    {(Array.isArray(accounts) ? accounts : [])
                        .filter(acc => acc.personId === (selectedPerson ? selectedPerson.id : null))
                        .map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                </select>

                {/* SÉLECTEUR D'ANNÉE */}
                <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    style={{ 
                        ...styles.select,
                        border: `1px solid ${themeColors.secondary}`, // Couleur dynamique
                        background: themeColors.inputBackground, // Couleur dynamique
                        color: themeColors.text, // Couleur dynamique
                    }}
                >
                    {years.map((y) => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
                
                {/* BOUTON DE PARAMÈTRES (SETTINGS) */}
                <Link to="/settings" style={styles.settingsButton} title="Paramètres du Compte">
                    <MdSettings size={24} style={{ color: isAssistanceMode ? '#fff' : themeColors.primary }} />
                </Link>

                {/* BOUTON DE DÉCONNEXION / SORTIE ASSISTANCE */}
                <button 
                    onClick={onLogout} 
                    style={{ ...styles.logoutButton, color: isAssistanceMode ? '#e74c3c' : themeColors.primary }}
                    title={isAssistanceMode ? "Quitter l'assistance" : "Déconnexion"}
                >
                    <MdLogout size={24} /> 
                </button>
            </div>
        </header>
    );
}

const styles = {
    topbar: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0px 20px",
        // backgroundColor est désormais géré dynamiquement
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)", 
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        height: "60px",
    },
    logoContainer: {
        display: 'flex',
        alignItems: 'center',
        marginRight: "20px",
    },
    headerLogoImage: {
        height: "55px",
        width: "auto",
    },
    links: {
        display: "flex",
        gap: "40px", 
        justifyContent: "center",
        flex: 1,
        height: '100%',
        alignItems: 'center',
    },
    link: {
        textDecoration: "none",
        fontSize: "1rem",
        fontWeight: "600",
        padding: '20px 0', 
        transition: "color 0.2s, border-bottom 0.2s",
    },
    dropdowns: {
        display: "flex",
        gap: "10px",
        marginLeft: "20px",
    },
    select: {
        padding: "5px 10px",
        borderRadius: "4px", 
        cursor: "pointer",
        fontWeight: 'bold',
        transition: 'border-color 0.2s, background-color 0.2s',
    },
    logoutButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '0 5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'color 0.2s',
    },
    settingsButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '0 5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textDecoration: 'none',
        transition: 'opacity 0.2s',
    },
    assistanceBadge: {
        backgroundColor: '#e74c3c',
        color: 'white',
        fontSize: '0.7rem',
        padding: '2px 8px',
        borderRadius: '10px',
        fontWeight: 'bold',
        marginLeft: '10px',
        whiteSpace: 'nowrap',
        animation: 'pulse 2s infinite'
    },
};